import React, { useEffect, useState } from "react";
import MetaTags from 'react-meta-tags';
import { Container, Modal, Row, Col } from "reactstrap";
import cookieHelper from "helpers/getCookieData";
import { get, post } from "../helpers/api_helper";
import { CLINIC_URL, PATIENT_URL } from "helpers/url_helper";
import { showSuccessAlert } from "pages/utils/alertMessages";
import Select from "react-select";
import { timeZones } from "assets/timezones";
import CountrySelector from "components/Common/CountrySelector";
import CurrencySelector from "components/Common/CurrencySelector";

// Icons from react-icons
import {
  RiHospitalLine,
  RiAddLine,
  RiMapPinLine,
  RiMailLine,
  RiPhoneLine,
  RiUserLine,
  RiArrowRightLine,
  RiCheckboxCircleLine,
  RiSearchLine,
  RiMailSendLine,
  RiAlertLine,
  RiBuilding4Line,
  RiRefreshLine,
} from "react-icons/ri";

import "./clinics.scss";

const WelcomeClinicScreen = () => {
  const [modalLarge, setModalLarge] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [defaultClinic, setDefaultClinic] = useState(null);
  const [formData, setFormData] = useState({});
  const [userData, setUserData] = useState(null);
  const [isVerified, setIsVerified] = useState(0); // 0: unverified, 1: verified, 2: blocked
  const [loader, setLoader] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [DDoptions, setDDOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  const detectTimezoneOffset = () => {
    try {
      const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (clientTz) {
        const found = timeZones.find(
          (t) => t.timezone.toLowerCase() === clientTz.toLowerCase()
        );
        if (found) return found.offset;
      }

      const offsetMinutes = -new Date().getTimezoneOffset();
      const sign = offsetMinutes >= 0 ? '+' : '-';
      const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, '0');
      const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
      const target = `${sign}${hours}:${minutes}`;

      const matchedOffset = timeZones.find(
        (t) => t.offset === target || t.offset?.includes(target)
      );
      return matchedOffset ? matchedOffset.offset : '+05:30';
    } catch (e) {
      return '+05:30';
    }
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const detectedTz = detectTimezoneOffset();
          
          // Reverse geocoding via OpenStreetMap Nominatim
          let resolved = false;
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
              {
                headers: {
                  'Accept': 'application/json',
                }
              }
            );
            
            if (res.ok) {
              const data = await res.json();
              const addr = data?.address || {};

              const city = addr.city || addr.town || addr.village || addr.suburb || addr.district || addr.county || '';
              const state = addr.state || addr.region || addr.state_district || '';
              const country = addr.country || 'India';
              const zip_code = addr.postcode || '';
              const streetRoad = [addr.road, addr.neighbourhood, addr.suburb].filter(Boolean).join(', ');

              setFormData(prev => ({
                ...prev,
                city: city || prev.city || '',
                state: state || prev.state || '',
                country: country || prev.country || 'India',
                zip_code: zip_code || prev.zip_code || '',
                address: prev.address || streetRoad || data?.display_name || '',
                time_zone: detectedTz || prev.time_zone || '+05:30',
              }));

              showSuccessAlert(`Location detected: ${[city, state, country, zip_code].filter(Boolean).join(', ')}`);
              resolved = true;
            }
          } catch (e) {
            console.warn('Nominatim error, falling back:', e);
          }

          if (!resolved) {
            // Fallback reverse geocoding
            const fbRes = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
            if (fbRes.ok) {
              const fbData = await fbRes.json();
              setFormData(prev => ({
                ...prev,
                city: fbData.city || fbData.locality || prev.city || '',
                state: fbData.principalSubdivision || prev.state || '',
                country: fbData.countryName || prev.country || 'India',
                zip_code: fbData.postcode || prev.zip_code || '',
                time_zone: detectedTz || prev.time_zone || '+05:30',
              }));
              showSuccessAlert(`Location detected: ${fbData.city || fbData.locality || ''}, ${fbData.principalSubdivision || ''}`);
            }
          }
        } catch (err) {
          console.error('Error in reverse geocoding:', err);
          alert('Could not fetch location details automatically. Please enter your address details manually.');
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (error) => {
        setIsDetectingLocation(false);
        console.warn('Geolocation error:', error);
        if (error.code === 1) {
          alert('Location permission was denied. Please allow location access in your browser or enter location manually.');
        } else {
          alert('Unable to retrieve your location. Please check your network/device location settings.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  };

  const fetchClinics = async () => {
    try {
      setLoader(true);
      let authData = null;
      try {
        const rawAuth = cookieHelper.getCookie('authUser');
        authData = rawAuth ? (typeof rawAuth === 'string' ? JSON.parse(rawAuth) : rawAuth) : null;
      } catch (err) {
        // ignore JSON parse error
      }
      const user = authData?.user || (authData?.user_id ? authData : null);
      setUserData(user);

      const res = await get('/auth/check-account-status');
      
      // Determine verification status: 1 = verified, 2 = blocked, 0 = pending
      let verifiedStatus = 0;
      if (res?.body?.is_verified !== undefined) {
        verifiedStatus = Number(res.body.is_verified);
      } else if (res?.body?.success !== undefined) {
        verifiedStatus = Number(res.body.success);
      } else if (res?.success === true || res?.success === 1) {
        verifiedStatus = 1;
      } else if (user?.is_verified === 1) {
        verifiedStatus = 1;
      }

      setIsVerified(verifiedStatus);

      // Only fetch clinics if email verification is completed
      if (verifiedStatus === 1) {
        const response = await get(`clinics?_q=${user?.user_id || ''}`);
        const items = response?.body?.items || (Array.isArray(response?.body) ? response.body : []) || (Array.isArray(response) ? response : []);
        setClinics(items);
        
        // If single clinic exists and not default, mark as default on backend
        if (items.length === 1 && items[0].is_default !== 1) {
          get(`clinics/defaultclinic?_default=1&clinic_id=${items[0].id}&client_id=${items[0].client_id}`);
        }

        const defaultItem = items.find(clinic => clinic.is_default === 1);
        setDefaultClinic(defaultItem?.id || (items.length === 1 ? items[0].id : null));
      }
    } catch (error) {
      console.error('Error checking verification or fetching clinics:', error);
      setIsVerified(0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchClinics();
  }, []);

  const handleDefaultChange = async (clinicData, e) => {
    e.stopPropagation();
    const isChecked = e.target.checked;
    const res = await get(`clinics/defaultclinic?_default=${isChecked ? 1 : 0}&clinic_id=${clinicData.id}&client_id=${clinicData.client_id}`);
    if (res?.success) {
      setDefaultClinic(isChecked ? clinicData.id : null);
      showSuccessAlert(isChecked ? 'Default clinic updated!' : 'Default clinic removed');
    }
  };

  const handleSelectClinic = (clinicData) => {
    cookieHelper.setCookie('_c', btoa(JSON.stringify(clinicData)), 1, 7);
    localStorage.setItem('clinic', clinicData?.id);
    localStorage.setItem('client', clinicData?.client_id);
    window.location.href = `/chairs`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.clinic_name) {
      alert("Please enter Clinic Name");
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await post(CLINIC_URL, formData);
      if (res?.success) {
        showSuccessAlert('Clinic created successfully!');
        setFormData({});
        toggleModal();
        fetchClinics();
      }
    } catch (err) {
      console.error('Error creating clinic:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDoctorSelect = (selectedOption) => {
    setSelectedDoc(selectedOption);
    setFormData((prev) => ({
      ...prev,
      doctor_code: selectedOption?.value,
      email: selectedOption?.email || prev.email || '',
      phone: selectedOption?.mobile || selectedOption?.phone || prev.phone || '',
    }));
  };

  const loadDocOptions = async () => {
    try {
      const { success, body } = await get(`${PATIENT_URL}/options?_type=doctors`);
      if (success && body && Array.isArray(body)) {
        setDDOptions(body);
        if (body.length > 0) {
          const firstDoc = body[0];
          setSelectedDoc(firstDoc);
          setFormData((prev) => ({
            ...prev,
            doctor_code: firstDoc?.value,
            email: prev.email || firstDoc?.email || '',
            phone: prev.phone || firstDoc?.mobile || firstDoc?.phone || '',
          }));
        }
      }
    } catch (err) {
      console.error('Error loading doctor options:', err);
    }
  };

  const toggleModal = () => {
    if (isVerified !== 1) {
      return;
    }
    const nextState = !modalLarge;
    setModalLarge(nextState);
    if (nextState) {
      const autoTz = detectTimezoneOffset();
      setFormData({
        time_zone: autoTz,
      });
      setSelectedDoc(null);
      loadDocOptions();
    }
  };

  const handleResendEmail = async () => {
    try {
      setResendingEmail(true);
      let emailToSend = userData?.email;
      if (!emailToSend) {
        try {
          const rawAuth = cookieHelper.getCookie('authUser');
          const authData = rawAuth ? (typeof rawAuth === 'string' ? JSON.parse(rawAuth) : rawAuth) : null;
          emailToSend = authData?.user?.email || authData?.email;
        } catch (e) {
          // ignore
        }
      }
      if (!emailToSend) {
        alert("Please log in to resend the verification email.");
        return;
      }
      const res = await get(`auth/resend-verification?email=${encodeURIComponent(emailToSend)}`);
      if (res?.success) {
        showSuccessAlert('Verification email resent! Please check your inbox.');
      }
    } catch (error) {
      console.error('Error resending verification:', error);
    } finally {
      setResendingEmail(false);
    }
  };

  const filteredClinics = clinics.filter((c) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      c.clinic_name?.toLowerCase().includes(term) ||
      c.doctor?.name?.toLowerCase().includes(term) ||
      c.address?.toLowerCase().includes(term) ||
      c.city?.toLowerCase().includes(term)
    );
  });

  return (
    <React.Fragment>
      <div className="page-content clinics-page-container">
        <MetaTags>
          <title>Clinic Workspaces | Oralstop OPD - Clinical Management System</title>
        </MetaTags>

        <Container fluid>
          {/* Header Section */}
          <div className="clinics-header-section">
            <div className="clinics-header-title-area">
              <div className="clinics-header-icon-box">
                <RiHospitalLine />
              </div>
              <div>
                <h1 className="clinics-main-title">Clinic Workspaces</h1>
                <p className="clinics-main-subtitle">
                  Select a clinical branch to manage dental chairs, doctor queues, and patient electronic records.
                </p>
              </div>
            </div>

            {/* ONLY SHOW SEARCH & ADD CLINIC ACTION IF EMAIL VERIFIED */}
            {isVerified === 1 && (
              <div className="clinics-header-actions">
                <div className="clinics-search-input-box">
                  <RiSearchLine />
                  <input
                    type="text"
                    className="form-control clinics-search-input"
                    placeholder="Search clinic or branch..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <button
                  type="button"
                  className="btn-add-clinic-primary"
                  onClick={toggleModal}
                >
                  <RiAddLine className="font-size-18" />
                  <span>Add New Clinic</span>
                </button>
              </div>
            )}
          </div>

          {/* Body Content */}
          {loader ? (
            <div className="clinics-loading-box">
              <div className="clinical-spinner" />
              <h5 className="font-size-16 font-weight-bold text-slate-800 mb-1">
                Loading Clinic Workspaces...
              </h5>
              <p className="text-muted font-size-13 mb-0">
                Checking verification status and loading clinic records
              </p>
            </div>
          ) : isVerified === 0 ? (
            /* EMAIL VERIFICATION REQUIRED STATE - NO ADD CLINIC SHOWN */
            <div className="verification-banner-card">
              <div className="verification-icon-circle">
                <RiMailSendLine />
              </div>
              <h4 className="font-weight-bold text-slate-800 mb-2">
                Email Verification Required
              </h4>
              <p className="text-muted font-size-14 mb-4">
                Please verify your email address to unlock your dental clinic management dashboard and register new clinics.
                Check your inbox for the verification link.
              </p>
              <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap">
                <button
                  type="button"
                  className="btn-add-clinic-primary"
                  onClick={handleResendEmail}
                  disabled={resendingEmail}
                >
                  <RiMailSendLine className="font-size-16" />
                  <span>{resendingEmail ? 'Sending...' : 'Resend Verification Email'}</span>
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 d-inline-flex align-items-center gap-2"
                  style={{ height: '42px', borderRadius: '10px', fontWeight: '600' }}
                  onClick={fetchClinics}
                >
                  <RiRefreshLine className="font-size-16" />
                  <span>I Verified My Email</span>
                </button>
              </div>
            </div>
          ) : isVerified === 2 ? (
            /* ACCOUNT SUSPENDED / BLOCKED STATE */
            <div className="verification-banner-card">
              <div className="verification-icon-circle" style={{ background: '#fee2e2', color: '#dc2626' }}>
                <RiAlertLine />
              </div>
              <h4 className="font-weight-bold text-danger mb-2">Account Suspended</h4>
              <p className="text-muted font-size-14 mb-0">
                This clinic account has been placed on hold. Please contact your clinical administrator.
              </p>
            </div>
          ) : (
            /* VERIFIED STATE - SHOW CLINIC CARDS AND ADD CLINIC CARD */
            <div className="clinic-card-grid">
              {/* Add New Clinic Card (Dashed) */}
              <div
                role="button"
                tabIndex={0}
                className="clinic-card-add-new"
                onClick={toggleModal}
                onKeyDown={(e) => { if (e.key === 'Enter') toggleModal(); }}
              >
                <div className="add-clinic-plus-circle">
                  <RiAddLine />
                </div>
                <h5 className="add-clinic-title">Register New Branch</h5>
                <p className="add-clinic-subtitle">
                  Add multi-chair dental branch, assign doctors & location settings.
                </p>
              </div>

              {/* Clinic Cards List */}
              {filteredClinics.map((clinic, index) => {
                const isDefault = defaultClinic === clinic.id;
                return (
                  <div
                    key={`CLINIC_${clinic.id || index}`}
                    role="button"
                    tabIndex={0}
                    className={`clinic-active-card ${isDefault ? 'is-default-clinic' : ''}`}
                    onClick={() => handleSelectClinic(clinic)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSelectClinic(clinic); }}
                  >
                    <div className="clinic-card-body">
                      <div className="clinic-card-top-row">
                        <div className="clinic-icon-badge">
                          <RiBuilding4Line />
                        </div>
                        <label
                          className="clinic-default-toggle-box"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isDefault}
                            onChange={(e) => handleDefaultChange(clinic, e)}
                          />
                          <span>{isDefault ? 'Default Clinic' : 'Set Default'}</span>
                        </label>
                      </div>

                      <h4 className="clinic-title-text text-truncate">
                        {clinic.clinic_name}
                      </h4>

                      {(clinic.doctors?.name || clinic.doctor?.name) && (
                        <div className="clinic-meta-row">
                          <RiUserLine />
                          <span className="font-weight-semibold">{clinic.doctors?.name || clinic.doctor?.name}</span>
                        </div>
                      )}

                      {clinic.address && (
                        <div className="clinic-meta-row text-truncate">
                          <RiMapPinLine />
                          <span>{clinic.address}{clinic.city ? `, ${clinic.city}` : ''}</span>
                        </div>
                      )}

                      <div className="clinic-meta-contact">
                        {clinic.phone && (
                          <span className="clinic-contact-pill">
                            <RiPhoneLine /> {clinic.phone}
                          </span>
                        )}
                        {clinic.email && (
                          <span className="clinic-contact-pill text-truncate" style={{ maxWidth: '170px' }}>
                            <RiMailLine /> {clinic.email}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="clinic-card-footer">
                      <div>
                        {clinic.status === 0 || clinic.status === 1 ? (
                          <span className="clinic-status-pill clinic-status-active">
                            <span className="pulse-dot" /> Active Branch
                          </span>
                        ) : (
                          <span className="clinic-status-pill clinic-status-inactive">
                            Inactive
                          </span>
                        )}
                      </div>

                      <button type="button" className="btn-open-workspace">
                        <span>Open Workspace</span>
                        <RiArrowRightLine />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Container>
      </div>

      {/* Add New Clinic Modal (Only accessible if verified) */}
      {isVerified === 1 && (
        <Modal
          size="lg"
          isOpen={modalLarge}
          toggle={toggleModal}
          className="custom-clinic-modal"
          centered
        >
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center gap-2">
                <RiHospitalLine className="text-primary font-size-22" />
                <h5 className="modal-title mb-0">Register New Clinic Branch</h5>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={toggleModal}
                aria-label="Close"
              />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <Row className="g-3">
                  <Col md={6}>
                    <label className="modal-input-label" htmlFor="modal-clinic-name">
                      Clinic Name *
                    </label>
                    <input
                      id="modal-clinic-name"
                      type="text"
                      className="form-control modal-input-field"
                      placeholder="e.g. Oralstop Dental Care"
                      name="clinic_name"
                      value={formData.clinic_name || ''}
                      onChange={handleChange}
                      required
                    />
                  </Col>

                  <Col md={6}>
                    <label className="modal-input-label" htmlFor="doctor">
                      Doctor in Charge
                    </label>
                    <Select
                      id="doctor"
                      className="basic-single"
                      classNamePrefix="select"
                      options={DDoptions}
                      value={selectedDoc || (DDoptions.length > 0 ? DDoptions[0] : null)}
                      onChange={handleDoctorSelect}
                      placeholder="Select Primary Doctor"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="modal-input-label" htmlFor="modal-email">
                      Clinic Email Address
                    </label>
                    <input
                      id="modal-email"
                      type="email"
                      className="form-control modal-input-field"
                      placeholder="clinic@oralstop.com"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col md={6}>
                    <label className="modal-input-label" htmlFor="modal-phone">
                      Clinic Contact Phone
                    </label>
                    <input
                      id="modal-phone"
                      type="tel"
                      className="form-control modal-input-field"
                      placeholder="+91 9876543210"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col md={12}>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <label className="modal-input-label mb-0" htmlFor="modal-address">
                        Street Address
                      </label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2 shadow-none"
                        style={{ borderRadius: '6px', fontSize: '0.78rem', fontWeight: '600' }}
                        onClick={handleGetLocation}
                        disabled={isDetectingLocation}
                      >
                        <RiMapPinLine />
                        <span>{isDetectingLocation ? 'Detecting Location...' : '📍 Auto-detect My Location'}</span>
                      </button>
                    </div>
                    <input
                      id="modal-address"
                      type="text"
                      className="form-control modal-input-field"
                      placeholder="123 Healthcare Ave, Suite 400"
                      name="address"
                      value={formData.address || ''}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col md={4}>
                    <label className="modal-input-label" htmlFor="modal-city">
                      City
                    </label>
                    <input
                      id="modal-city"
                      type="text"
                      className="form-control modal-input-field"
                      placeholder="Mumbai"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col md={4}>
                    <label className="modal-input-label" htmlFor="modal-state">
                      State / Region
                    </label>
                    <input
                      id="modal-state"
                      type="text"
                      className="form-control modal-input-field"
                      placeholder="Maharashtra"
                      name="state"
                      value={formData.state || ''}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col md={4}>
                    <label className="modal-input-label" htmlFor="modal-zip">
                      Zip / Postal Code
                    </label>
                    <input
                      id="modal-zip"
                      type="text"
                      className="form-control modal-input-field"
                      placeholder="400001"
                      name="zip_code"
                      value={formData.zip_code || ''}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col md={4}>
                    <label className="modal-input-label" htmlFor="modal-country">
                      Country
                    </label>
                    <CountrySelector
                      id="modal-country"
                      value={formData.country_code || 'IN'}
                      onCountrySelect={(defaults) => {
                        setFormData((prev) => ({
                          ...prev,
                          country: defaults.name,
                          country_code: defaults.code,
                          time_zone: defaults.default_timezone,
                          currency_code: defaults.default_currency,
                        }));
                      }}
                    />
                  </Col>

                  <Col md={4}>
                    <label className="modal-input-label" htmlFor="modal-timezone">
                      Time Zone
                    </label>
                    <input
                      id="modal-timezone"
                      type="text"
                      className="form-control modal-input-field"
                      placeholder="Asia/Kolkata"
                      name="time_zone"
                      value={formData.time_zone || 'Asia/Kolkata'}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col md={4}>
                    <label className="modal-input-label" htmlFor="modal-currency">
                      Operating Currency
                    </label>
                    <CurrencySelector
                      id="modal-currency"
                      value={formData.currency_code || 'INR'}
                      onChange={(e) => setFormData((prev) => ({ ...prev, currency_code: e.target.value }))}
                    />
                  </Col>
                </Row>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={toggleModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-add-clinic-primary"
                  disabled={isSubmitting}
                >
                  <RiCheckboxCircleLine className="font-size-18" />
                  <span>{isSubmitting ? 'Registering...' : 'Save Clinic Branch'}</span>
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default WelcomeClinicScreen;
